// @ts-nocheck
export function isLocalDev() {
  const h = location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h.endsWith(".localhost");
}

/** Signed-in game_master accounts. */
export function isGameMasterAccount(account) {
  return !!(account && account.role === "game_master");
}

/** Signed-in admin accounts (pre-alpha gate bypass). */
export function isAdminAccount(account) {
  return !!(account && account.role === "admin");
}

/** Signed-in admin/game_master accounts (round dev cheats work online for them). */
export function hasDevRoundCheatAccount(account) {
  return isAdminAccount(account) || isGameMasterAccount(account);
}

/* ---------------- Config / tuning ---------------- */
export const DISCORD_INVITE_URL = "https://discord.gg/aM26Ga6cs";

export const CFG = {
  // Match
  roundsPerMatch: 10,    // fixed-length match; champion = most round wins
  startGold: 50,         // gold every player begins the match with (host-configurable)
  practiceStartGold: 300, // default for Practice vs Bots (PVE) solo setup
  minStartGold: 0,
  maxStartGold: 10000,

  // Arena
  worldRadius: 950,      // fixed world radius in world units — SINGLE source of truth for arena
                         // size (mirrors server MaxRadius). Never derive this from screen size.
  viewRadius: 640,       // world half-span always visible along the shorter screen axis. The
                         // render scale (zoom) is derived from this so every device shows the
                         // same slice of the world; a small screen just draws it smaller.
  viewTilt: 0.64,        // oblique camera: vertical foreshortening of the ground plane (1 = pure
                         // top-down, lower = more "tilted away"). The world circle renders as an
                         // ellipse; characters/pillars keep full height (billboarded). CLIENT-ONLY.
  arenaMinFrac: 0.14,    // platform shrinks down to this fraction of the max radius
  // Staged shrink (mirror server): hold full for shrinkStartDelay, then step
  // through shrinkStages, transitioning over shrinkStep and holding
  // shrinkHolds[i] before each next step.
  shrinkStartDelay: 10,  // seconds the platform holds at full size before the first shrink (PvE uses pveShrinkStartDelay)
  pveShrinkStartDelay: 55,
  pveShrinkHoldMult: 2,
  shrinkStep: 3,         // seconds each shrink transition takes
  shrinkHolds: [12, 18, 18], // seconds held at each stage before the next shrink (mid stages linger)
  shrinkStages: [0.8, 0.6, 0.3, 0.14], // radius targets (fractions of max), in order
  pveMinFrac: 0.3,       // PvE boss fights stop shrinking here (mirror server PvEMinFrac); FFA still uses shrinkStages floor

  // Arena visuals (CLIENT RENDER-ONLY — never sent by the server / catalog).
  // Sell a floating-3D "stone slab" with a top-down ortho camera: an extruded
  // cliff lip on the lower rim + a drop shadow into the void. Lit from upper-left
  // to match the sprites/pillars. Tune freely; these never touch gameplay.
  platformDepth: 0.22,   // how far the rocky underside hangs below the top face (fraction of radius) — main 3D knob
  platformViz: {
    cliffTop: "#2a2640",       // shaded top of the rock wall — kept DARKER than the top face so the
                               // underside reads as shadow, not as a lit surface (the bright accent is
                               // the thin lip rim in drawSlabRim, not the wall itself)
    cliffMid: "#151227",       // mid rock tone (quick falloff so the wall reads as a chunky body)
    cliffBot: "#060410",       // shaded base of the wall, fading into the void
    edgeAO:   "rgba(0,0,0,0.5)",    // ambient-occlusion darkening at the top-face rim (raised look)
    bevel:    "rgba(208,216,255,0.26)", // cool highlight on the upper-left top lip
    lip:      "rgba(176,168,205,0.58)", // soft stone lip where the top face meets the wall
    seam:     "rgba(0,0,0,0.28)",   // dark vertical crevices between rock facets
    spikeCount: 0,             // stalactites hanging from the underside (0 = none)
    spikeLen: 0.7,             // stalactite length as a fraction of platformDepth
  },
  starParallax: 0.07,    // fraction of camera offset the starfield drifts (floating-over-void depth)
  voidDebrisParallax: [0.05, 0.50], // per-rock parallax range (screen-space void rubble)
  voidDebrisSize: [2, 26],          // px; skewed toward smaller shards at spawn
  voidDebrisDrift: [6, 28],         // px/s screen drift (scaled by parallax depth)
  voidShootInterval: [12, 20],       // seconds between shooting-star spawn attempts
  voidShootChance: 0.72,            // probability when an attempt fires
  voidShootSpeed: [240, 420],       // px/s — slow drift reads as distant

  // Pillars (stone columns that block projectiles and bodies)
  pillarCount: 9,        // how many pillars to scatter each round
  pillarMinR: 28,
  pillarMaxR: 52,

  // Camera
  camLerp: 6,            // how snappily the camera follows the player
  roundSpectateDelayMs: 4000, // after elimination, delay before auto spectate view
  hudCornerBaseY: 48,          // top-right stack: gold / dmg (below center round title)
  hudCornerLineH: 20,
  hudWinsBaseY: 48,            // left standings list (moved down with corner stack)
  scoreboardSideTop: 100,      // docked scoreboard Y (below top HUD)
  scoreboardSideTopSpec: 120,  // extra offset when spectator HUD lines show
  faceTurnRate: 14,      // rad/s the wizard turns to face its walk direction (matches sim FaceTurnRate)
  moveAlignAngle: 0.35,  // rad; must face within this of move dir before locomotion accel (matches sim)

  // Health / falling
  maxHP: 1000,           // large pool (Warlock-style): damage chips, knockback kills
  outsideDrainRate: 198, // HP/s at full void ramp (~5.8s full-HP death; matches server/sim)
  outsideDrainMin: 62,   // void drain the instant you step off the platform
  voidRampTime: 2.2,     // seconds over the void to ramp drain from min to full
  voidMoveMult: 0.75,    // locomotion accel over the void (matches server/sim VoidMoveMult)
  voidFaceTurnMult: 0.70, // facing turn rate over the void (matches server/sim VoidFaceTurnMult)
  insideRegen: 0,        // HP regained per second while safely on the platform (0 = no regen)

  // Knockback is derived from damage: launch = damage * knockPerDamage * (1 + lowHpKnock*(1 - hp/maxHP)) * (baseMass/mass)
  knockPerDamage: 8,     // tamed for 4x damage (was 30): lethal damage, calmer launches
  lowHpKnock: 1.6,       // extra launch multiplier at 0 HP (Smash-style: low HP flies further)
  baseMass: 100,         // reference mass; items raise/lower it to resist/amplify knockback
  maxSpells: 9,          // a player can own up to this many spells (keys 1-9)

  // Player physics
  playerRadius: 23,
  moveAccel: 2400,       // high accel + high friction => snappy, no "walking on ice" drift
  friction: 9.0,         // walking friction (quick stop). Knockback uses knockFriction.
  knockFriction: 2.0,    // low friction while being knocked back, so launches still slide far
  knockWindow: 0.6,      // seconds a hit keeps you in "knockback" (low-friction) mode
  knockStack: 1.5,       // extra launch multiplier if hit again while already being knocked back
  arriveRadius: 7,
  hitStunLook: 3.0,      // how long "last hit by" stays valid for kill credit

  // Economy — SP spells, PP passives, Sy synergies, gold items
  goldKill: 20,          // per KO, paid at round end to the killing-blow dealer
  goldBase: 30,          // floor everyone earns each round
  goldWin: 20,           // bonus to the round winner (last alive) only
  goldDamageDivisor: 30, // gold earned per this much damage dealt (1000 HP bar ≈ 33g)
  goldCatchupPerWin: 9,  // anti-snowball: gold per round win the player trails the leader by
  startSkillPoints: 0,
  startPassivePoints: 0,
  startSynergyPoints: 0,

  // Bots
  botBaseGold: 0,
};

/**
 * Client-only scorch marks left on the arena floor by explosions.
 * r / rMul: world-radius of the settled burn (r when no blast radius is passed;
 * blastR * rMul when the server sends a detonation radius).
 * spreadDur: seconds for the splatter to snap outward (keep short — reads instant).
 * intensity: peak darkness (0–1).
 * dust: particle count scale for the outward puff on spawn.
 */
export const EXPLOSION_BURN = {
  nuke:       { r: 240, rMul: 0.58, life: 52, spreadDur: 0.14, intensity: 0.9,  dust: 1.0 },
  meteor:     { r: 168, rMul: 0.84, life: 38, spreadDur: 0.11, intensity: 0.74, dust: 0.9 },
  scourge:    { r: 148, rMul: 0.9,  life: 34, spreadDur: 0.1,  intensity: 0.72, dust: 0.85 },
  projectile: { r: 44,  rMul: 0.36, life: 24, spreadDur: 0.07, intensity: 0.5,  dust: 0.55 },
  projColl:   { r: 92,  rMul: 0.92, life: 30, spreadDur: 0.09, intensity: 0.6,  dust: 0.75 },
  obstacle:   { r: 88,  rMul: 6.2, life: 24, spreadDur: 0.06, intensity: 0.52, dust: 0.72, fanArc: 1.18 },
  firewall:   { r: 38,  life: 32, spreadDur: 0.05, intensity: 0.5,  dust: 0.32, step: 36 },
};

/** Pick a burn preset from a projectile detonation radius (world units). */
export function explosionBurnKindForBlast(blastR) {
  if (blastR >= 300) return "nuke";
  if (blastR >= 170) return "meteor";
  return "projectile";
}

/** Per-boss PvE arena presentation (CLIENT RENDER-ONLY). */
export const ARENA_THEMES = {
  default: {
    voidFill: "#05060d",
    starTint: "#cdd6ff",
    ambient: { kind: "none", color: "#cdd6ff" },
  },
  angel: {
    voidFill: "#0d0806",
    starTint: "#ffd4a8",
    viz: {
      cliffTop: "#3a2218",
      cliffMid: "#221510",
      cliffBot: "#080504",
      lip: "rgba(255,180,120,0.85)",
      bevel: "rgba(255,160,100,0.22)",
      seam: "rgba(0,0,0,0.32)",
    },
    floorWash: {
      kind: "radial",
      inner: "rgba(255,120,40,0.14)",
      mid: "rgba(180,60,20,0.07)",
      outer: "rgba(80,30,10,0)",
      innerR: 0.12,
    },
    ambient: { kind: "embers", color: "#ff8844", count: 48, rise: true },
  },
  titan: {
    voidFill: "#080706",
    starTint: "#c8b8a0",
    viz: {
      cliffTop: "#322820",
      cliffMid: "#1a1410",
      cliffBot: "#060504",
      lip: "rgba(180,160,140,0.80)",
      bevel: "rgba(200,180,160,0.20)",
    },
    floorWash: {
      kind: "radial",
      inner: "rgba(120,90,60,0.12)",
      mid: "rgba(80,60,40,0.06)",
      outer: "rgba(40,30,20,0)",
      innerR: 0.08,
    },
    ambient: { kind: "motes", color: "#a89070", count: 36, rise: false },
  },
  aurelion: {
    voidFill: "#0a0804",
    starTint: "#ffe8a0",
    viz: {
      cliffTop: "#3a3018",
      cliffMid: "#221a0c",
      cliffBot: "#080604",
      lip: "rgba(255,220,140,0.88)",
      bevel: "rgba(255,230,160,0.24)",
    },
    floorWash: {
      kind: "radial",
      inner: "rgba(255,220,100,0.18)",
      mid: "rgba(200,160,60,0.08)",
      outer: "rgba(120,90,30,0)",
      innerR: 0.2,
    },
    ambient: { kind: "motes", color: "#ffd060", count: 40, rise: true },
  },
  groshkar: {
    voidFill: "#060a06",
    starTint: "#a8d8a0",
    viz: {
      cliffTop: "#1a2818",
      cliffMid: "#101a10",
      cliffBot: "#040804",
      lip: "rgba(140,200,120,0.82)",
      bevel: "rgba(120,180,100,0.20)",
      seam: "rgba(0,0,0,0.34)",
    },
    sideTint: { color: "#142018", alpha: 0.24 },
    floorWash: {
      kind: "radial",
      inner: "rgba(80,160,60,0.14)",
      mid: "rgba(50,100,40,0.08)",
      outer: "rgba(30,60,20,0)",
      innerR: 0.15,
    },
    ambient: { kind: "spores", color: "#88cc70", count: 48, rise: false },
  },
  vorathal: {
    voidFill: "#080a14",
    starTint: "#b8d8f0",
    viz: {
      cliffTop: "#1a2840",
      cliffMid: "#121830",
      cliffBot: "#080a14",
      lip: "rgba(168,212,240,0.85)",
      bevel: "rgba(168,212,240,0.22)",
      seam: "rgba(0,0,0,0.32)",
    },
    sideTint: { color: "#1a2840", alpha: 0.28 },
    floorWash: {
      kind: "spokes",
      inner: "rgba(200,240,255,0.12)",
      mid: "rgba(168,212,240,0.08)",
      outer: "rgba(100,180,230,0)",
      innerR: 0.15,
      spokeColor: "rgba(232,244,255",
      spokeCount: 5,
    },
    ambient: { kind: "snow", color: "#e8f4ff", count: 48, rise: false },
  },
  ixtholoth: {
    voidFill: "#0a0518",
    starTint: "#c8a0e8",
    viz: {
      cliffTop: "#281840",
      cliffMid: "#181028",
      cliffBot: "#080410",
      lip: "rgba(160,120,220,0.85)",
      bevel: "rgba(140,100,200,0.22)",
    },
    floorWash: {
      kind: "rift",
      radius: 0.34,
      inner: "rgba(18,8,28,0.72)",
      mid: "rgba(58,24,96,0.38)",
      outer: "rgba(106,56,168,0.14)",
      edge: "rgba(58,24,96,0)",
      strokeColor: "rgba(106,56,168",
    },
    ambient: { kind: "motes", color: "#9060c8", count: 32, rise: true },
  },
  kael: {
    voidFill: "#08060e",
    starTint: "#c0b0f0",
    viz: {
      cliffTop: "#281840",
      cliffMid: "#181028",
      cliffBot: "#060410",
      lip: "rgba(180,160,240,0.85)",
      bevel: "rgba(160,140,220,0.22)",
    },
    floorWash: {
      kind: "radial",
      inner: "rgba(120,80,220,0.14)",
      mid: "rgba(80,50,160,0.07)",
      outer: "rgba(40,20,80,0)",
      innerR: 0.1,
    },
    ambient: { kind: "motes", color: "#a080e8", count: 44, rise: true },
  },
  sisters: {
    voidFill: "#0a0810",
    starTint: "#e8c8ff",
    floorWash: {
      kind: "split",
      nw: "rgba(232,184,64,0.22)",
      ne: "rgba(74,24,96,0.24)",
    },
    ambient: { kind: "motes", color: "#d8b0ff", count: 36, rise: true },
  },
};

/** Practice / lobby bot AI tiers (server: sim.BotSkill*). */
export const BOT_SKILLS = [
  { id: "noob", label: "Noob", hint: "Me bot, me play game, beep boop!" },
  { id: "novice", label: "Novice", hint: "Fairly easy" },
  { id: "master", label: "Master", hint: "Skilled player" },
  { id: "archmage", label: "Archmage", hint: "Do you hate your life?" },
];

export function normalizeBotSkill(id) {
  const s = String(id || "").toLowerCase();
  return BOT_SKILLS.some(t => t.id === s) ? s : "master";
}

export function botSkillLabel(id) {
  const tier = BOT_SKILLS.find(t => t.id === normalizeBotSkill(id));
  return tier ? tier.label : "Master";
}

/** In-game suffix for AI warlocks, e.g. " (Bot - Master)". */
export function playerBotTag(pl) {
  if (!pl?.isBot) return "";
  if (pl.boss || pl.bossAdd) return "";
  return ` (Bot - ${botSkillLabel(pl.botSkill)})`;
}

/** Title position constants (mirror server achievements.TitlePos). */
export const TITLE_PREFIX = 1;
export const TITLE_SUFFIX = 2;

const guestSuffix = " (guest)";

/** Capitalize the display handle (stored lowercase); keeps guest suffix as-is. */
export function capitalizeDisplayName(name) {
  if (!name) return "";
  let base = name;
  let suffix = "";
  if (name.toLowerCase().endsWith(guestSuffix)) {
    base = name.slice(0, -guestSuffix.length);
    suffix = guestSuffix;
  }
  base = base.trim();
  if (!base) return name;
  return base.charAt(0).toUpperCase() + base.slice(1) + suffix;
}

/** Plain-text player name with optional cosmetic title (no comma). Prefix: "Starbreaker Olle". Suffix: "Olle the Inferno-Touched". */
export function withTitle(name, title, titlePos) {
  const n = capitalizeDisplayName(name || "");
  if (!title) return n;
  return titlePos === TITLE_SUFFIX ? `${n} ${title}` : `${title} ${n}`;
}

/** Achievement id for the signed-in player's equipped title (empty = none). */
export function equippedTitleId(account) {
  return (account && account.equippedTitle) || "";
}

/* ---------------- Spell definitions ----------------
   Spells deal DAMAGE; knockback is derived from damage and the target's HP
   (lower HP -> bigger launch). Upgrades scale damage and shorten cooldown. */
// Full handbook roster grouped into fixed slots (columns). One spell per slot.
// Mirrors server/game.go.
export const SPELLS = {
  fireball:  { name: "Fireball", fallbackIcon: "🔥", slot: "1", color: "#ff7a2f", type: "projectile", cost: 0, cooldown: 3.2, speed: 540, life: 2.2, radius: 13, damage: 72, stun: 0.35, desc: "Default primary. A heavy, hard-hitting bolt." },
  freeze:    { name: "Freeze", fallbackIcon: "❄️", slot: "1", color: "#67e8ff", type: "projectile", cost: 100, cooldown: 3.2, speed: 880, life: 1.3, radius: 9, damage: 16, rootDur: 1.2, silenceDur: 1.2, desc: "Replaces Fireball: freezes the enemy (root + silence)." },
  icelance:  { name: "Ice Lance", fallbackIcon: "🧊", slot: "1", color: "#8fd6ff", type: "projectile", cost: 100, cooldown: 5.0, speed: 540, life: 2.4, radius: 14, damage: 58, stun: 0.3, slowDur: 2.8, slowMult: 0.5, desc: "Replaces Fireball: a heavy ice lance that chills (slows) on hit." },
  scourge:   { name: "Scourge", fallbackIcon: "💥", slot: "2", color: "#ff5d3a", type: "burst", cost: 0, cooldown: 20, windup: 1.5, range: 165, damage: 150, selfDamage: 50, stun: 0.3, desc: "Channel 1.5s (no walking; mobility spells still work), then explode: shove enemies, hurt yourself." },
  sanctuary: { name: "Sanctuary", fallbackIcon: "✨", slot: "2", color: "#7ee8a0", type: "burst", cost: 0, cooldown: 22, windup: 1.5, range: 165, heal: 52, desc: "Channel 1.5s (locked), then release a holy pulse that mends nearby allies and yourself." },
  frostnova: { name: "Frost Nova", fallbackIcon: "🧊", slot: "2", color: "#7ee8ff", type: "burst", cost: 100, cooldown: 14, range: 140, freezeDur: 1.2, desc: "Replaces Scourge: instant frost burst around you. Freezes enemies in place (no damage); frozen foes cannot use mobility spells. Each level: −1s cooldown, +10 range, +0.2s freeze." },
  lightning: { name: "Lightning", fallbackIcon: "⚡", slot: "3", color: "#67e8ff", type: "hitscan", cost: 70, cooldown: 3.6, range: 540, damage: 36, stun: 0.15, desc: "Instantly strikes the nearest enemy in your aim." },
  voidbeam:  { name: "Void Beam", fallbackIcon: "🌌", slot: "3", color: "#2a1840", type: "chargebeam", cost: 85, cooldown: 9, windup: 1.4, range: 620, radius: 12, detRadius: 48, damage: 118, stun: 0.28, desc: "Hold to charge a devastating line of void along your aim. Longer charge reaches farther and hits a wider line. Firing slows you and exhausts your cooldowns. Release early for a weaker blast, or hold to the max (auto-fires at full charge)." },
  homing:    { name: "Homing", fallbackIcon: "🛰️", slot: "3", color: "#b76bff", type: "projectile", cost: 90, cooldown: 9.6, speed: 500, life: 2.8, radius: 11, damage: 44, stun: 0.28, homing: true, desc: "Projectile that locks onto the nearest enemy ahead, then leads their movement.", searchKeywords: ["seeker", "tracking"] },
  boomerang: { name: "Boomerang", fallbackIcon: "🪃", slot: "3", color: "#ffd479", type: "projectile", cost: 90, cooldown: 10.4, speed: 700, life: 2.6, radius: 11, damage: 40, stun: 0.2, boomerang: true, pierce: 99, desc: "Curves out and back; hits repeatedly." },
  chain:     { name: "Chain Spark", fallbackIcon: "🌩️", slot: "3", color: "#9fd0ff", type: "chain", cost: 90, cooldown: 7.0, range: 520, damage: 34, stun: 0.12, chain: 3, chainRange: 300, chainFalloff: 0.72, desc: "Strikes the nearest enemy, then arcs to up to 3 more nearby (damage falls off each hop)." },
  chainmend: { name: "Chain Mend", fallbackIcon: "💚", slot: "3", color: "#7ee8a0", type: "chainheal", cost: 95, cooldown: 9.0, range: 480, heal: 38, chain: 2, chainRange: 280, chainFalloff: 0.80, desc: "Mends the nearest injured ally, then jumps to up to 2 more nearby teammates (healing falls off each hop)." },
  splitter:  { name: "Splitter", fallbackIcon: "✳️", slot: "4", color: "#ffd479", type: "projectile", cost: 90, cooldown: 20, speed: 820, life: 1.6, radius: 10, damage: 40, stun: 0.2, split: 6, desc: "Bursts into shards on impact." },
  meteor:    { name: "Meteor", fallbackIcon: "☄️", slot: "Q", color: "#ff6a3a", type: "projectile", cost: 110, cooldown: 28, range: 340, life: 1.4, radius: 16, stun: 0.45, detRadius: 200, detDamage: 96, fall: true, desc: "Calls a meteor down from the sky onto a spot; it crashes after a short delay for a big blast." },
  blizzard:  { name: "Blizzard", fallbackIcon: "🌨️", slot: "4", color: "#7ec8ff", type: "blizzard", cost: 105, cooldown: 24, range: 340, detRadius: 75, duration: 5.0, damage: 38, slowDur: 0.35, slowMult: 0.52, desc: "Conjure a blizzard at your cursor; icy shards rain for 5 seconds, damaging and chilling enemies inside." },
  bloompod:  { name: "Bloom Pod", fallbackIcon: "🌱", slot: "4", color: "#8ef0a8", type: "healpod", cost: 105, cooldown: 21, range: 330, armDelay: 2.2, detRadius: 120, heal: 50, damage: 28, stun: 0.12, desc: "Plant a swelling spore at your cursor; after a short delay it bursts, mending nearby allies and lightly harming foes. Allies see the blast radius while it grows." },
  windwalk:  { name: "Wind Walk", fallbackIcon: "🌫️", slot: "R", color: "#cdd6ff", type: "self", cost: 90, cooldown: 44, duration: 4, invis: true, damage: 80, stun: 0.4, desc: "Turn invisible and move faster; slam the first enemy you touch (breaks stealth).", searchKeywords: ["invis", "stealth"] },
  illusionist: { name: "Illusionist", fallbackIcon: "🎭", slot: "R", color: "#c9a0ff", type: "illusion", cost: 95, cooldown: 38, duration: 10, desc: "Spawn 2 decoys that mimic your name and HP at cast time, move like you, and vanish on any hit or after 10 seconds (cannot cast). Mirror Army passive adds more decoys.", searchKeywords: ["decoy", "clone"] },
  frozenorb: { name: "Frozen Orb", fallbackIcon: "🔵", slot: "4", color: "#5fd0ff", type: "projectile", cost: 120, cooldown: 26, speed: 300, life: 2.6, radius: 18, damage: 90, stun: 0.3, slowDur: 2.5, slowMult: 0.45, pierce: 99, spike: "frostspike", spikeEvery: 0.2, spikeCount: 8, desc: "Ultimate: hurl a spinning orb of frost that sprays icy spikes in every direction. The orb hits hard, knocks back and chills heavily; the spikes chip and briefly slow." },
  frostspike:{ name: "Frost Spike", fallbackIcon: "❄️", color: "#aef0ff", type: "projectile", speed: 430, life: 0.45, radius: 7, damage: 11, stun: 0.1, slowDur: 0.8, slowMult: 0.6, desc: "A small shard of frost flung off a Frozen Orb." },
  voidglass_shard:{ name: "Voidglass Shard", fallbackIcon: "🧊", color: "#b8e8ff", type: "projectile", speed: 560, life: 0.95, radius: 5, damage: 240, stun: 0.12, slowDur: 0.45, slowMult: 0.7, desc: "Razor fragment sheared from a Voidglass orb." },
  voidorb:   { name: "Void Orb", fallbackIcon: "🌌", color: "#7b5cff", type: "projectile", radius: 18, desc: "An unstable orb of raw void from the Void Maelstrom synergy." },
  swap:      { name: "Swap", fallbackIcon: "🔄", slot: "R", color: "#c98bff", type: "projectile", cost: 90, cooldown: 36, speed: 1100, life: 1.2, radius: 12, swap: true, desc: "Swap places with the enemy you hit.", searchKeywords: ["switch", "blink"] },
  bonespear: { name: "Bone Spear", fallbackIcon: "🦴", slot: "4", color: "#f4f0e8", type: "projectile", cost: 95, cooldown: 22, windup: 0.8, speed: 2000, life: 1.6, radius: 10, damage: 96, stun: 0.44, selfHemorrhageDur: 5.0, selfHemorrhageDmgPerUnit: 0.14, selfSlowDur: 5.0, selfSlowMult: 0.70, cdPenaltyDur: 5.0, cdPenaltyAdd: 3.0, desc: "Flay the flesh of your arm, snap the bone, and hurl it as a deadly spear. You and anyone struck bleed for 5s — the wound only hurts when they move; casting exhausts you (+3s cooldown per cast for 5s)." },
  teleport:  { name: "Teleport", fallbackIcon: "✨", slot: "R", color: "#ffe089", type: "self", cost: 60, cooldown: 18, distance: 320, desc: "Blink toward the cursor.", searchKeywords: ["blink", "flash"] },
  thrust:    { name: "Thrust", fallbackIcon: "🚀", slot: "R", color: "#9ab8ff", type: "dash", cost: 80, cooldown: 8, distance: 1000, damage: 44, stun: 0.3, desc: "Dash forward; slam enemies you hit.", searchKeywords: ["dash"] },
  drain:     { name: "Drain", fallbackIcon: "🩸", slot: "Q", color: "#ff5d7a", type: "projectile", cost: 90, cooldown: 12, speed: 820, life: 1.4, radius: 10, damage: 32, stun: 0.12, drainHeal: 0.7, slowDur: 1.0, slowMult: 0.55, desc: "Damages + slows; heals you.", searchKeywords: ["lifesteal", "vamp"] },
  bouncer:   { name: "Bouncer", fallbackIcon: "🔮", slot: "Q", color: "#a0ffd0", type: "projectile", cost: 90, cooldown: 16, speed: 880, life: 2.6, radius: 9, damage: 32, stun: 0.18, bounce: 4, pierce: 99, bounceDmg: 0.35, desc: "Ricochets off walls and players, hitting harder after every bounce." },
  firespray: { name: "Fire Spray", fallbackIcon: "🎆", slot: "Q", color: "#ff9a5a", type: "projectile", cost: 90, cooldown: 16, speed: 600, life: 1.0, radius: 9, damage: 24, stun: 0.12, count: 5, spread: 0.5, desc: "A fan of small fireballs." },
  rush:      { name: "Rush", fallbackIcon: "🏃", slot: "R", color: "#9affb0", type: "self", cost: 80, cooldown: 15, duration: 3, desc: "Take less knockback and move faster.", searchKeywords: ["haste", "speed"] },
  shield:    { name: "Shield", fallbackIcon: "🛡️", slot: "E", color: "#7db4ff", type: "self", cost: 70, cooldown: 20, duration: 1.8, desc: "Bubble that absorbs all knockback.", searchKeywords: ["bubble"] },
  barrier:   { name: "Arcane Barrier", fallbackIcon: "🫧", slot: "E", color: "#c9a0ff", type: "self", cost: 90, cooldown: 20, duration: 5.0, absorb: 260, desc: "Raise a ward that soaks up to 260 incoming damage for a few seconds (knockback still applies).", searchKeywords: ["ward"] },
  reflect:   { name: "Reflect Shield", fallbackIcon: "🪞", slot: "E", color: "#9fe7ff", type: "self", cost: 95, cooldown: 22, duration: 2.0, desc: "Raise a mirror that bounces incoming projectiles back at their caster (and eats their damage + knockback).", searchKeywords: ["mirror"] },
  timeshift: { name: "Time Shift", fallbackIcon: "⏳", slot: "E", color: "#c9a0ff", type: "self", cost: 110, cooldown: 20, duration: 2.0, desc: "Rewind to where you were, restoring HP.", searchKeywords: ["rewind"] },
  disable:   { name: "Disable", fallbackIcon: "🕸️", slot: "W", color: "#a0ffd0", type: "projectile", cost: 90, cooldown: 32, speed: 980, life: 1.2, radius: 9, damage: 12, rootDur: 1.4, desc: "Roots the enemy in place.", searchKeywords: ["root"] },
  gravity:   { name: "Black Hole", fallbackIcon: "🕳️", slot: "W", color: "#b06bff", type: "snare", cost: 90, cooldown: 24, range: 320, armDelay: 0.4, duration: 2.6, radius: 280, pullWell: 2400, desc: "Tear open a black hole on the ground; it drags every enemy in a wide radius toward its core." },
  hook:      { name: "Hook", fallbackIcon: "🪝", slot: "W", color: "#c9a86a", type: "projectile", cost: 95, cooldown: 15, speed: 615, life: 0.775, radius: 13, damage: 50, noKnock: true, tetherDur: 2.4, desc: "Throw a hook on a chain. Hit a foe or let them run into it — either way, reel them all the way to you.", searchKeywords: ["grapple"] },
  link:      { name: "Link", fallbackIcon: "⛓️", slot: "W", color: "#ff5d7a", type: "projectile", cost: 100, cooldown: 36, speed: 980, life: 1.4, radius: 10, damage: 16, pull: true, tetherDur: 2.0, desc: "Hook + tether: reel them in, bleed them.", searchKeywords: ["grapple", "hook"] },
  snare:     { name: "Sigil Snare", fallbackIcon: "🪤", slot: "W", color: "#b98bff", type: "snare", cost: 90, cooldown: 18, range: 260, armDelay: 0.7, duration: 5.0, radius: 72, rootDur: 1.1, slowDur: 0.8, slowMult: 0.5, desc: "Plant a rune; after a short arm it roots and chills enemies who linger inside.", searchKeywords: ["trap"] },
  voidrip:   { name: "Void Rip", fallbackIcon: "🌑", slot: "W", color: "#2a1840", type: "voidrip", cost: 95, cooldown: 22, range: 300, armDelay: 0.55, duration: 4.5, radius: 88, damage: 260, pullWell: 1800, desc: "Tear open the arena at your cursor. The outer crack pulls victims inward; only the black void core at the center drains damage. Warning: you are affected too." },
  firewall:  { name: "Firewall", fallbackIcon: "🧱", slot: "Q", color: "#ff7a2f", type: "firewall", cost: 100, cooldown: 14, range: 220, radius: 24, damage: 50, duration: 3.0, distance: 360, desc: "Conjure a wall of fire at your cursor, set crossways to your aim so it blocks a lane. Enemies standing in the flames take heavy damage, and anyone who touches it catches fire and keeps burning after they leave.", searchKeywords: ["wall"] },
  glacier:   { name: "Glacier", fallbackIcon: "🧊", slot: "W", color: "#8fdcff", type: "icewall", cost: 95, cooldown: 22, range: 68, radius: 38, duration: 3.5, distance: 280, desc: "Raise a solid block of ice crossways to your aim. Nothing walks through it; only Teleport crosses. Projectiles hit it like any other obstacle. Spec Ice Age for +1s uptime.", searchKeywords: ["wall", "ice wall"] },
};
export const SPELL_ORDER = ["fireball","freeze","icelance","scourge","sanctuary","frostnova","lightning","voidbeam","homing","boomerang","chain","chainmend","splitter","meteor","blizzard","bloompod","frozenorb","bonespear","swap","teleport","thrust","windwalk","illusionist","drain","bouncer","firespray","firewall","glacier","rush","shield","timeshift","barrier","reflect","disable","gravity","hook","link","snare","voidrip"];
// Mirrors server/sim/spell_desc.go until catalog merges descShort.
export const SPELL_DESC_SHORT_FALLBACK = {
  fireball: "Heavy, hard-hitting bolt.",
  freeze: "Fast bolt that roots and silences.",
  icelance: "Ice lance that chills on hit.",
  scourge: "Channel, then explode — hurts you too.",
  sanctuary: "Channel, then heal nearby allies.",
  frostnova: "Instant freeze burst around you.",
  lightning: "Instant bolt on nearest foe in aim.",
  voidbeam: "Hold to charge a void line blast; firing slows you and exhausts cooldowns.",
  homing: "Projectile that chases enemies.",
  boomerang: "Curves out and back; multi-hit.",
  chain: "Arcs damage across nearby foes.",
  chainmend: "Healing jumps between allies.",
  splitter: "Shatters into shards on impact.",
  meteor: "Delayed sky crash at your cursor.",
  blizzard: "5s icy zone — damage and chill.",
  bloompod: "Delayed heal burst at your cursor.",
  windwalk: "Invisible sprint; slam on contact.",
  illusionist: "Spawn decoys that mimic you.",
  frozenorb: "Orb sprays frost spikes everywhere.",
  frostspike: "Shard sprayed from Frozen Orb.",
  voidglass_shard: "Razor fragment from a Voidglass orb.",
  voidorb: "Unstable void orb from Void Maelstrom.",
  swap: "Swap places with whoever you hit.",
  bonespear: "Hit + self bleed on movement; exhaustion +3s CD (5s).",
  teleport: "Blink toward the cursor.",
  thrust: "Dash forward and slam foes.",
  drain: "Damage, slow, and heal yourself.",
  bouncer: "Ricochets harder each bounce.",
  firespray: "Fan of small fireballs.",
  rush: "Less knockback taken; move faster.",
  shield: "Bubble that blocks knockback.",
  barrier: "Ward that soaks incoming damage.",
  reflect: "Mirror that returns projectiles.",
  timeshift: "Rewind position and restore HP.",
  disable: "Roots the enemy in place.",
  gravity: "Ground vortex that pulls everyone in.",
  hook: "Chain hook — reel foes to you.",
  link: "Hook, tether, and reel them in.",
  snare: "Rune that roots lingerers.",
  voidrip: "Cursor tear — pulls you in too.",
  firewall: "Burning wall across your aim.",
  glacier: "Solid ice block — only Teleport passes.",
};
// Pseudo-spell ids used only for damage attribution in the combat report — these
// are not castable spells, so they live outside SPELLS / SPELL_ORDER.
export const DMG_SOURCES = { fireburn: { name: "Burning", icon: "🔥", color: "#ff7a3a" } };
// PvE boss abilities (presentation only — logic lives in server/sim).
export const PVE_BOSS_SPELLS = {
  boss_seraph_bolt:     { name: "Ember Lance", fallbackIcon: "🔥", color: "#ff8844", radius: 18 },
  boss_divine_well:     { name: "Pyre Well", fallbackIcon: "🌀", color: "#ff7a2f", radius: 130 },
  boss_radiant_burst:   { name: "Scorch Burst", fallbackIcon: "🔥", color: "#ff6030" },
  boss_judgment_nova:   { name: "Inferno Nova", fallbackIcon: "💥", color: "#ff5020" },
  boss_add_fireball:    { name: "Ember Bolt", fallbackIcon: "🔥", color: "#ff8844", radius: 13 },
  boss_seismic_slam:    { name: "Fault Line", fallbackIcon: "💥", color: "#c8a882" },
  boss_fissure:         { name: "Ground Fissure", fallbackIcon: "🌋", color: "#8a7058" },
  boss_boulder:         { name: "Boulder Toss", fallbackIcon: "🪨", color: "#b8c4e8", radius: 22, fall: true, life: 1.3, detRadius: 85 },
  boss_colossus_charge: { name: "Colossus Charge", fallbackIcon: "🐂", color: "#b8c4e8" },
  boss_lighthouse_sweep: { name: "Sweeping Lance", fallbackIcon: "☀", color: "#ffc040" },
  boss_ping_bolt:        { name: "Sun Ping", fallbackIcon: "✴", color: "#ffd060", radius: 15 },
  boss_solar_well:       { name: "Eclipse Well", fallbackIcon: "🌑", color: "#c890ff" },
  boss_solar_flare:      { name: "Solar Pulse", fallbackIcon: "💫", color: "#ffd878" },
  boss_arcane_barrage:   { name: "Arcane Barrage", fallbackIcon: "✦", color: "#b080ff", radius: 11 },
  boss_blink_strike:     { name: "Blink Strike", fallbackIcon: "⚡", color: "#c090ff" },
  boss_mirror_court:     { name: "Mirror Court", fallbackIcon: "🪞", color: "#a070e0" },
  boss_spatial_swap:     { name: "Spatial Swap", fallbackIcon: "🔄", color: "#d0a0ff" },
  boss_arcane_detonation: { name: "Arcane Detonation", fallbackIcon: "💜", color: "#9040d0" },
  boss_mirror_bolt:      { name: "Mirror Bolt", fallbackIcon: "✦", color: "#c0a0ff", radius: 10 },
  boss_rime_lance:       { name: "Rime Lance", fallbackIcon: "❄", color: "#a8d4f0", radius: 16 },
  boss_glacial_drift:    { name: "Glacial Drift", fallbackIcon: "🧊", color: "#9fe7ff", radius: 110 },
  boss_howling_gale:     { name: "Howling Gale", fallbackIcon: "💨", color: "#b8e8ff" },
  boss_frigid_well:      { name: "Frigid Well", fallbackIcon: "🧊", color: "#c8a0ff" },
  boss_flash_freeze:     { name: "Flash Freeze", fallbackIcon: "❄", color: "#c8f0ff" },
  boss_shatter:          { name: "Shatter", fallbackIcon: "💎", color: "#e8f4ff" },
  boss_add_frostbolt:    { name: "Frost Lance", fallbackIcon: "🧊", color: "#a8d4f0", radius: 14 },
  boss_putrid_spew:      { name: "Putrid Spew", fallbackIcon: "☣", color: "#6a8a30", radius: 85 },
  boss_poison_spray:     { name: "Poison Spray", fallbackIcon: "🎆", color: "#6a8a30", radius: 10 },
  boss_trample:           { name: "Trample", fallbackIcon: "🦶", color: "#8a7058" },
  boss_contagion:        { name: "Contagion", fallbackIcon: "🦠", color: "#c8d040", desc: "Pool-borne plague — ticks damage and spreads to nearby allies every pulse." },
  boss_gorge:            { name: "Gorge", fallbackIcon: "🩸", color: "#6a8a30" },
  boss_plague_burst:     { name: "Plague Burst", fallbackIcon: "💀", color: "#4a2848" },
  boss_void_lash:        { name: "Void Lash", fallbackIcon: "🌀", color: "#6a38a8", radius: 14 },
  boss_devouring_maw:    { name: "Devouring Maw", fallbackIcon: "🌑", color: "#4a1860" },
  boss_void_bloom:       { name: "Void Bloom", fallbackIcon: "🌸", color: "#9a40e0", radius: 16 },
  boss_mind_lash:        { name: "Mind Lash", fallbackIcon: "🧠", color: "#8040b0" },
  boss_dominate_mind:    { name: "Dominate Mind", fallbackIcon: "👁‍🗨", color: "#a050d0" },
  boss_holy_bolt:        { name: "Holy Bolt", fallbackIcon: "✨", color: "#e8b840", radius: 15 },
  boss_void_bolt:        { name: "Void Bolt", fallbackIcon: "🌑", color: "#8040a0", radius: 15 },
  boss_banishing_pull:   { name: "Banishing Pull", fallbackIcon: "🌀", color: "#4a1860", radius: 115 },
  boss_radiant_sweep:    { name: "Radiant Sweep", fallbackIcon: "☀", color: "#ffd878" },
  boss_essence_bloom:    { name: "Essence Bloom", fallbackIcon: "🌸", color: "#e8b840" },
  boss_essence_meridian: { name: "Essence Meridian", fallbackIcon: "☯", color: "#c8a0ff" },
};
// Resolve a damage-source id to its display info (real spell, boss ability, or pseudo-source).
export function spellInfo(id) { return SPELLS[id] || PVE_BOSS_SPELLS[id] || DMG_SOURCES[id] || null; }
export const SLOT_ORDER = ["1","2","3","4","Q","W","E","R","Space"];
export const LOADOUT = { maxEquipped: 8 };
export const START_LOADOUT = { "1": "fireball", "2": "scourge", "Space": "teleport" };
export const PVE_START_LOADOUT = { "1": "fireball", "2": "sanctuary", "Space": "teleport" };
/** Shop category display order (overwritten by catalog on join). */
export const SHOP_CATEGORY_ORDER = [
  "fire", "frost", "selfaoe", "lightning", "void", "bolts", "control", "defense", "mobility", "support",
];
// Presentation for spell damage schools (authoritative ids come from the catalog).
export const SPELL_SCHOOLS = {
  fire:      { label: "Fire",      color: "#ff7a2f" },
  frost:     { label: "Frost",     color: "#67e8ff" },
  void:      { label: "Void",      color: "#9a6bff" },
  lightning: { label: "Lightning", color: "#ffe566" },
  arcane:    { label: "Arcane",    color: "#c9a0ff" },
  physical:  { label: "Physical",  color: "#b8c4e8" },
  holy:      { label: "Holy",      color: "#fff0a8" },
  nature:    { label: "Nature",    color: "#7dff9a" },
};
// Shop spell categories (authoritative ids come from the catalog).
export const SPELL_SHOP_CATEGORIES = {
  fire:      { label: "Fire",      color: "#ff7a2f" },
  frost:     { label: "Frost",     color: "#67e8ff" },
  selfaoe:   { label: "Self AoE",  color: "#ff6b6b" },
  lightning: { label: "Lightning", color: "#ffe566" },
  void:      { label: "Void",      color: "#9a6bff" },
  bolts:     { label: "Bolts",     color: "#b8c4e8" },
  control:   { label: "Control",   color: "#7dff9a" },
  defense:   { label: "Defense",   color: "#c9a0ff" },
  mobility:  { label: "Mobility",  color: "#5eead4" },
  support:   { label: "Support",   color: "#fff0a8" },
};
// Functional spell categories (gameplay rules, e.g. mobility during scourge channel).
export const SPELL_CATEGORIES = {
  mobility: { label: "Mobility", color: "#5eead4" },
};
// Status debuff labels for spell tooltips (ids match server/sim SpellDebuffTag).
export const SPELL_DEBUFFS = {
  hemorrhage: { label: "Hemorrhage", color: "#ff6b6b" },
  slow:       { label: "Slow",       color: "#67e8ff" },
  exhaustion: { label: "Exhaustion", color: "#9a93c8" },
  root:       { label: "Root",       color: "#7dff9a" },
  silence:    { label: "Silence",    color: "#c9a0ff" },
  freeze:     { label: "Freeze",     color: "#aef0ff" },
  burn:       { label: "Burn",       color: "#ff7a2f" },
  tether:     { label: "Tether",     color: "#b8c4e8" },
};
const DEBUFF_TAG_ORDER = ["hemorrhage", "slow", "exhaustion", "root", "silence", "freeze", "burn", "tether"];

function fmtDurSec(d) {
  if (!d || d <= 0) return "0s";
  if (Math.abs(d - Math.round(d)) < 0.05) return `${Math.round(d)}s`;
  return `${d.toFixed(1)}s`;
}

function fmtMovePct(mult) {
  if (!mult || mult <= 0 || mult >= 1) return "slows movement";
  return `${Math.round(mult * 100)}% move speed`;
}

/** Mirror of server/sim debuffDetail for tooltip level scaling. */
export function debuffDetailFor(s, id, scope, lvl = 1) {
  if (!s) return "";
  const L = lvl < 1 ? 1 : lvl;
  switch (id) {
    case "hemorrhage":
      return `${fmtDurSec(s.selfHemorrhageDur)} — ${s.selfHemorrhageDmgPerUnit} HP lost per unit moved`;
    case "exhaustion":
      return `${fmtDurSec(s.cdPenaltyDur)} — +${s.cdPenaltyAdd}s cooldown per cast`;
    case "slow":
      if ((scope === "self" || scope === "both") && s.selfSlowDur > 0) {
        return `${fmtDurSec(s.selfSlowDur)} — ${fmtMovePct(s.selfSlowMult)}`;
      }
      if (s.slowDur > 0) {
        let line = `${fmtDurSec(s.slowDur)} — ${fmtMovePct(s.slowMult)}`;
        if (s.type === "blizzard" && s.duration > 0) line += ", refreshes while in zone";
        else if (s.type === "snare") line += " while standing in rune";
        return line;
      }
      break;
    case "root": {
      let line = `${fmtDurSec(s.rootDur)} — cannot move`;
      if (s.type === "snare") line += " while in rune";
      return line;
    }
    case "silence":
      return `${fmtDurSec(s.silenceDur)} — cannot cast spells`;
    case "freeze":
      return `${fmtDurSec(s.freezeDur)} — cannot move; blocks mobility spells`;
    case "burn": {
      const dps = (s.damage || 0) * 0.5;
      return `${fmtDurSec(1.6)} — ${Math.round(dps)} burn DPS after touching wall`;
    }
    case "tether":
      return `${fmtDurSec(s.tetherDur)} — hooked and reeled toward caster`;
  }
  return "";
}

function scanDebuffScopes(s) {
  const self = {};
  const enemy = {};
  if (s.selfHemorrhageDur > 0 && s.selfHemorrhageDmgPerUnit > 0) {
    self.hemorrhage = true;
    enemy.hemorrhage = true;
  }
  if (s.selfSlowDur > 0) self.slow = true;
  if (s.cdPenaltyDur > 0 && s.cdPenaltyAdd > 0) self.exhaustion = true;
  if (s.rootDur > 0) enemy.root = true;
  if (s.silenceDur > 0) enemy.silence = true;
  if (s.freezeDur > 0) enemy.freeze = true;
  if (s.slowDur > 0) enemy.slow = true;
  if (s.tetherDur > 0) enemy.tether = true;
  if (s.type === "firewall") enemy.burn = true;
  return { self, enemy };
}

/** Debuff badges for a spell; prefers catalog debuffTags, falls back to field scan. */
export function spellDebuffTags(s) {
  if (!s) return [];
  return spellDebuffTagsForTip(s, 1);
}

/** Debuff rows with detail text for tooltips (recomputes detail at spell level). */
export function spellDebuffTagsForTip(s, lvl = 1) {
  if (!s) return [];
  const L = lvl < 1 ? 1 : lvl;
  let tags;
  if (Array.isArray(s.debuffTags) && s.debuffTags.length) {
    tags = s.debuffTags.map((t) => ({ scope: t.scope, id: t.id, detail: t.detail || "" }));
  } else {
    const { self, enemy } = scanDebuffScopes(s);
    tags = [];
    for (const id of DEBUFF_TAG_ORDER) {
      const onSelf = !!self[id];
      const onEnemy = !!enemy[id];
      if (onSelf && onEnemy) tags.push({ scope: "both", id });
      else if (onSelf) tags.push({ scope: "self", id });
      else if (onEnemy) tags.push({ scope: "enemy", id });
    }
  }
  return tags.map((t) => ({
    scope: t.scope,
    id: t.id,
    detail: debuffDetailFor(s, t.id, t.scope, L) || t.detail || "",
  }));
}
Object.keys(SPELLS).forEach(id => { SPELLS[id].id = id; });

// Optional per-spell SFX (client presentation). Omit a slot or set "" = no sound.
// cast — on cast; impact — on hit; loop — lingering zone/beam; end — zone expires (e.g. void rip pop).
export const SPELL_SFX_SLOTS = ["cast", "impact", "loop", "end"];
export const SFX_CAST_PROJECTILE = "audio/sfx/cast/cast_06.mp3";
export const SPELL_TYPE_SFX = {
  projectile: { cast: SFX_CAST_PROJECTILE },
};
export const SPELL_SFX = {
  frostspike: { cast: "" }, // orb child shards — no cast sting
  scourge: { cast: "audio/sfx/impact/impact_fire_01.mp3" }, // plays when channel completes (scourge FX)
  firewall: { cast: "audio/sfx/cast/cast_14.mp3", loop: "audio/sfx/cast/cast_firewall.mp3" },
  glacier: { cast: "audio/sfx/cast/cast_06.mp3" },
  voidrip: { cast: "audio/sfx/cast/cast_18.mp3", end: "audio/sfx/cast/cast_19.mp3" },
};
export const FIRE_IMPACT_SFX = [
  "audio/sfx/impact/impact_fire_02.mp3",
  "audio/sfx/impact/impact_fire_03.mp3",
  "audio/sfx/impact/impact_fire_04.mp3",
];
export const SPELL_IMPACT_POOLS = {
  fireball: FIRE_IMPACT_SFX,
  firespray: FIRE_IMPACT_SFX,
  meteor: FIRE_IMPACT_SFX,
};
let firesprayImpactSeq = 0;
export function spellSfx(spellId) {
  const out = { cast: "", impact: "", loop: "", end: "" };
  const sp = SPELLS[spellId];
  if (!sp) return out;
  const typePack = SPELL_TYPE_SFX[sp.type];
  if (typePack) {
    for (const slot of SPELL_SFX_SLOTS) {
      if (typePack[slot]) out[slot] = typePack[slot];
    }
  }
  const over = SPELL_SFX[spellId];
  if (over) {
    for (const slot of SPELL_SFX_SLOTS) {
      if (over[slot] !== undefined) out[slot] = over[slot];
    }
  }
  return out;
}
export function allSpellSfxPaths() {
  const paths = new Set();
  for (const pack of [...Object.values(SPELL_TYPE_SFX), ...Object.values(SPELL_SFX)]) {
    for (const slot of SPELL_SFX_SLOTS) {
      if (pack[slot]) paths.add(pack[slot]);
    }
  }
  for (const pool of Object.values(SPELL_IMPACT_POOLS)) {
    for (const p of pool) paths.add(p);
  }
  return paths;
}

export const COMING_SOON_DESC = "Warlocks are still figuring this one out — Coming soon.";
export const SPELL_AVAIL = {};
export const spellAvail = (id) => SPELL_AVAIL[id] || "";
export const spellVisible = (id) => SPELL_ORDER.includes(id) && spellAvail(id) !== "hidden";

/** One-line blurb for shop cards and compact lists (catalog: descShort). */
export function spellDescShort(id) {
  const s = SPELLS[id];
  if (!s) return "";
  if (spellAvail(id) === "coming_soon") return COMING_SOON_DESC;
  return s.descShort || SPELL_DESC_SHORT_FALLBACK[id] || s.desc || "";
}

/** Full blurb for hover tooltips and spellbook detail (catalog: desc). */
export function spellDescDetail(id) {
  const s = SPELLS[id];
  if (!s) return "";
  if (spellAvail(id) === "coming_soon") return COMING_SOON_DESC;
  return s.desc || s.descShort || "";
}

export const spellShopDesc = spellDescShort;

/** Presentation-only aliases for spellbook/shop type-to-search. */
export function spellSearchAliases(id) {
  const kw = SPELLS[id]?.searchKeywords;
  if (!kw) return "";
  return (Array.isArray(kw) ? kw : [kw]).join(" ");
}

export const spellShopPurchasable = (id, matchMode) => {
  const st = spellAvail(id);
  if (st === "hidden" || st === "coming_soon") return false;
  if (st === "disabled" && matchMode !== "sandbox") return false;
  return true;
};
export const SPELLS_IN_CATEGORY = (cat) => SPELL_ORDER.filter(id => SPELLS[id]?.shopCategory === cat && spellVisible(id));
/** @deprecated use SPELLS_IN_CATEGORY */
export const SPELLS_IN_SLOT = SPELLS_IN_CATEGORY;

export function shopCategoryLabel(cat) {
  return SPELL_SHOP_CATEGORIES[cat]?.label || cat;
}

export function equippedSpellCount(loadout) {
  let n = 0;
  for (const slot of SLOT_ORDER) {
    if (slot === "Space") continue;
    if (loadout?.[slot]) n++;
  }
  return n;
}

export function isBarFull(loadout) {
  return equippedSpellCount(loadout) >= LOADOUT.maxEquipped;
}

export function nextFreeSlot(loadout) {
  if (isBarFull(loadout)) return "";
  for (const slot of SLOT_ORDER) {
    if (slot === "Space") continue;
    if (!loadout?.[slot]) return slot;
  }
  return "";
}

export function slotOfSpell(loadout, spellId) {
  for (const slot of SLOT_ORDER) {
    if (loadout?.[slot] === spellId) return slot;
  }
  return "";
}

// Report center categories (tags are toggles; details are free text).
export const REPORT_CATEGORIES = {
  spells: {
    label: "Spells",
    hint: "Balance, clarity, visuals, or audio for a specific spell.",
    spellSelect: true,
    tags: [
      { id: "too_weak", label: "Too weak" },
      { id: "too_strong", label: "Too strong" },
      { id: "visual", label: "Visual problem" },
      { id: "audio", label: "Audio problem" },
      { id: "unclear", label: "Hard to understand" },
      { id: "bug", label: "Bug / broken" },
      { id: "other", label: "Other" },
    ],
  },
  gameplay: {
    label: "Gameplay",
    hint: "Knockback, arena, movement, bots, items, economy, or overall feel.",
    tags: [
      { id: "knockback", label: "Knockback / physics" },
      { id: "arena", label: "Arena shrink / void" },
      { id: "movement", label: "Movement / dash" },
      { id: "bots", label: "Bots / AI" },
      { id: "items", label: "Items / passives" },
      { id: "economy", label: "Gold / shop" },
      { id: "teams", label: "Teams / FFA" },
      { id: "too_slow", label: "Too slow paced" },
      { id: "too_fast", label: "Too hectic" },
      { id: "fun", label: "Feels great" },
      { id: "bug", label: "Bug" },
      { id: "other", label: "Other" },
    ],
  },
  ui: {
    label: "UI",
    hint: "Menus, HUD, shop, readability, or layout.",
    tags: [
      { id: "hud", label: "In-match HUD" },
      { id: "shop_ui", label: "Shop / spellbook" },
      { id: "lobby", label: "Lobby / menus" },
      { id: "readability", label: "Hard to read" },
      { id: "layout", label: "Overlapping / clipped" },
      { id: "colors", label: "Colors / contrast" },
      { id: "tooltips", label: "Tooltips / help" },
      { id: "bug", label: "Bug" },
      { id: "other", label: "Other" },
    ],
  },
  sound: {
    label: "Sound",
    hint: "Volume, missing sounds, music, or combat SFX.",
    tags: [
      { id: "too_loud", label: "Too loud" },
      { id: "too_quiet", label: "Too quiet" },
      { id: "missing", label: "Missing sound" },
      { id: "wrong", label: "Wrong sound plays" },
      { id: "music", label: "Music" },
      { id: "sfx_combat", label: "Combat SFX" },
      { id: "sfx_ui", label: "UI SFX" },
      { id: "bug", label: "Bug" },
      { id: "other", label: "Other" },
    ],
  },
  network: {
    label: "Network / Lag",
    hint: "Ping, rubber-banding, disconnects, or desync.",
    tags: [
      { id: "high_ping", label: "High ping" },
      { id: "rubber_band", label: "Rubber-banding" },
      { id: "disconnect", label: "Disconnects" },
      { id: "desync", label: "Desync / wrong state" },
      { id: "input_lag", label: "Input lag" },
      { id: "shop_delay", label: "Slow shop / ready" },
      { id: "bug", label: "Bug" },
      { id: "other", label: "Other" },
    ],
  },
  other: {
    label: "Other",
    hint: "Anything else — please describe what happened.",
    tags: [],
  },
};
export const REPORT_CAT_ORDER = ["spells", "gameplay", "ui", "sound", "network", "other"];

// ---- Menu prefs (create-game + matchmaking UI; per-device localStorage) ----
export const CREATE_PREFS_LS = "wlk_create_prefs";
export const MM_PREFS_LS = "wlk_mm_prefs";

/** FFA matchmaker: every selectable player count (2–8). "Any" = all of these. */
export const MM_FFA_ALL_SIZES = [2, 3, 4, 5, 6, 7, 8];

/** PvE matchmaker: every selectable player count (2–4). "Any" = all of these. */
export const MM_PVE_ALL_SIZES = [2, 3, 4];

/** Home-screen quick match: FFA lobbies from 2 up to 4 players. */
export const MM_QUICK_MATCH_SIZES = [2, 3, 4];

export function mmFfaSizesIsAny(sizes) {
  const set = new Set(sizes);
  return MM_FFA_ALL_SIZES.every(n => set.has(n));
}

export function mmPveSizesIsAny(sizes) {
  const set = new Set(sizes);
  return MM_PVE_ALL_SIZES.every(n => set.has(n));
}

/** Teams matchmaker: every selectable team size (2v2–4v4). "Any" = all of these. */
export const MM_TEAM_ALL_SIZES = [2, 3, 4];

export function mmTeamSizesIsAny(sizes) {
  const set = new Set(sizes);
  return MM_TEAM_ALL_SIZES.every(n => set.has(n));
}

export function loadCreatePrefs() {
  const d = {
    public: true,
    bots: 0,
    botSkill: "novice",
    rounds: CFG.roundsPerMatch,
    startGold: CFG.startGold,
    mode: "skilldraft",
  };
  try {
    const raw = JSON.parse(localStorage.getItem(CREATE_PREFS_LS) || "null");
    if (!raw || typeof raw !== "object") return d;
    const rounds = Math.round(Number(raw.rounds));
    const startGold = Math.round(Number(raw.startGold));
    const bots = Math.round(Number(raw.bots));
    return {
      public: raw.public !== false,
      bots: Math.max(0, Math.min(7, Number.isFinite(bots) ? bots : d.bots)),
      botSkill: normalizeBotSkill(raw.botSkill || d.botSkill),
      rounds: Math.max(1, Math.min(25, Number.isFinite(rounds) ? rounds : d.rounds)),
      startGold: Math.max(CFG.minStartGold, Math.min(CFG.maxStartGold,
        Number.isFinite(startGold) ? startGold : d.startGold)),
      mode: ["normal", "allrandom", "skilldraft", "pve"].includes(raw.mode) ? raw.mode : d.mode,
    };
  } catch (e) { return d; }
}

export function saveCreatePrefs(prefs) {
  try { localStorage.setItem(CREATE_PREFS_LS, JSON.stringify(prefs)); } catch (e) {}
}

export function loadMmPrefs() {
  const d = {
    category: "pvp",
    format: "ffa",
    playerSizes: [...MM_FFA_ALL_SIZES],
    pvePlayerSizes: [...MM_PVE_ALL_SIZES],
    teamSizes: [...MM_TEAM_ALL_SIZES],
    teamCounts: [2, 3, 4],
    modes: ["skilldraft"],
  };
  const pickInts = (arr, min, max, fallback) => {
    if (!Array.isArray(arr)) return fallback;
    const out = [...new Set(arr.map(n => Math.round(Number(n))).filter(n => n >= min && n <= max))].sort((a, b) => a - b);
    return out.length ? out : fallback;
  };
  try {
    const raw = JSON.parse(localStorage.getItem(MM_PREFS_LS) || "null");
    if (!raw || typeof raw !== "object") return d;
    return {
      category: raw.category === "pve" ? "pve" : "pvp",
      format: raw.format === "teams" ? "teams" : "ffa",
      playerSizes: pickInts(raw.playerSizes, 2, 8, d.playerSizes),
      pvePlayerSizes: pickInts(raw.pvePlayerSizes, 2, 4, d.pvePlayerSizes),
      teamSizes: pickInts(raw.teamSizes, 2, 4, d.teamSizes),
      teamCounts: pickInts(raw.teamCounts, 2, 4, d.teamCounts),
      modes: (() => {
        if (!Array.isArray(raw.modes)) return d.modes;
        const m = raw.modes
          .map(x => (x === "normal" ? "skilldraft" : x))
          .filter(x => x === "skilldraft");
        return m.length ? m : d.modes;
      })(),
    };
  } catch (e) { return d; }
}

export function saveMmPrefs(prefs) {
  try { localStorage.setItem(MM_PREFS_LS, JSON.stringify(prefs)); } catch (e) {}
}

// ---- Keybinds (client-only presentation; the server only ever receives spell
// ids, never keys). Each spell slot ("1".."E") is fired by a key the player can
// remap. Binds are stored per-device in localStorage; the default binds each
// slot to the key of the same label. ----
export const DEFAULT_BINDS = Object.fromEntries(SLOT_ORDER.map(s => [s, s]));
export const KEYBINDS_LS = "warlock_keybinds";
// Keys reserved for other UI (scoreboard hold, dialog confirm/cancel) — refused
// as binds so remapping can't shadow them.
export const RESERVED_KEYS = new Set(["Tab", "Escape", "Enter"]);
export const KEY_GLYPH = { Space: "Space", ArrowUp: "↑", ArrowDown: "↓", ArrowLeft: "←", ArrowRight: "→" };

export const KEYBINDS = loadKeybinds();   // { slot: keyLabel }
export let KEY_TO_SLOT = {};              // { keyLabel: slot }
rebuildKeyToSlot();

export function loadKeybinds() {
  const binds = { ...DEFAULT_BINDS };
  try {
    const raw = JSON.parse(localStorage.getItem(KEYBINDS_LS) || "{}");
    SLOT_ORDER.forEach(s => { if (typeof raw[s] === "string" && raw[s]) binds[s] = raw[s]; });
  } catch (e) {}
  return binds;
}
export function saveKeybinds() { try { localStorage.setItem(KEYBINDS_LS, JSON.stringify(KEYBINDS)); } catch (e) {} }
export function rebuildKeyToSlot() {
  KEY_TO_SLOT = {};
  SLOT_ORDER.forEach(s => { KEY_TO_SLOT[KEYBINDS[s] || s] = s; });
}
// Canonical label for a keyboard event, matching how binds are stored.
export function eventKeyLabel(e) {
  if (e.key === " " || e.code === "Space") return "Space";
  if (e.key && e.key.length === 1) return e.key.toUpperCase();
  return e.key || "";
}
export function displayKey(k) { return KEY_GLYPH[k] || k; }
export function keyLabelForSlot(slot) { return displayKey(KEYBINDS[slot] || slot); }

// Passive items bought in the shop. Mirrors server/items.go.
export const ITEMS = {
  boots:         { name: "Boots of Speed", icon: "👟", cost: 120, stackable: true, desc: "+move speed, but lighter (more knockback)." },
  lavatreads:    { name: "Lava Treads", icon: "🥾", cost: 120, stackable: true, desc: "+move speed." },
  pendant:       { name: "Pendant", icon: "📿", cost: 100, stackable: true, desc: "+max HP (tankier, harder to launch)." },
  cape:          { name: "Cape", icon: "🧣", cost: 120, stackable: true, desc: "+HP regen while on the platform." },
  helmet:        { name: "Helmet", icon: "⛑️", cost: 130, stackable: true, desc: "+mass (resist knockback), -spell range." },
  bloodsword:    { name: "Blood Sword", icon: "🗡️", cost: 150, stackable: true, desc: "Lifesteal: heal from damage you deal." },
  soj:           { name: "Stone of Jordan", icon: "💍", cost: 220, stackable: true, desc: "+20% damage on all your spells." },
  pocketwatch:   { name: "Pocket Watch", icon: "⏱️", cost: 120, stackable: false, desc: "Curses on you end sooner." },
  stafffireball: { name: "Staff of Fireball", icon: "🪄", cost: 150, stackable: false, desc: "Fireball sets enemies on fire." },
};
export const ITEM_ORDER = ["boots","lavatreads","pendant","cape","helmet","bloodsword","soj","pocketwatch","stafffireball"];
export const itemCost = (it, owned) => Math.round(it.cost * Math.pow(UPGRADE.itemCostGrowth, owned));

// Item economy scaling (overwritten at runtime by catalog upgrade block).
export const UPGRADE = {
  itemCostGrowth: 1.4,
  maxItems: 6,
};

// Skill / passive / synergy economy (overwritten by catalog skillPoints).
export const SKILL_POINTS = {
  start: 0,
  startPassive: 0,
  startSynergy: 0,
  sandboxStart: 99,
  sandboxStartPassive: 99,
  sandboxStartSynergy: 99,
  spPerRound: 1,
  ppPerRound: 1,
  syEveryNRounds: 4,
  synergyCost: 1,
  tiers: [{ maxGoldCost: 99999, unlock: 1 }],
};

export function spellSpTier(goldCost) {
  for (const t of SKILL_POINTS.tiers) {
    if (goldCost <= t.maxGoldCost) return t.unlock;
  }
  return SKILL_POINTS.tiers[SKILL_POINTS.tiers.length - 1]?.unlock ?? 1;
}

export function spellUnlockSp(spell) {
  return spellSpTier(spell?.cost ?? 0);
}

/** SP to buy a spell onto the action bar. */
export function spellCommitSp(spell, owned) {
  if (owned) return 0;
  return spellUnlockSp(spell);
}

/** PP cost for a stat passive; Sy cost for synergy nodes (always 1 each). */
export function passiveBuyCost(node) {
  if (!node) return 0;
  if (node.synergy) return SKILL_POINTS.synergyCost ?? 1;
  return 1;
}

export function passiveBuyLabel(node) {
  const cost = passiveBuyCost(node);
  if (node?.synergy) return `${cost} Sy`;
  return `${cost} PP`;
}

const PASSIVE_STAT_LABELS = {
  damage: "DMG",
  cooldown: "CD",
  knock: "Knock",
  speed: "Speed",
  radius: "Radius",
  range: "Range",
  duration: "Dur",
  heal: "Heal",
  absorb: "Absorb",
  illusions: "Decoys",
  split: "Shards",
  bounce: "Bounce",
  armDelay: "Arm",
};

/** Compact improvement lines for a passive node (−12% CD, +25% DMG, …). */
export function passiveBonusLines(node) {
  if (!node) return [];
  const parts = [];
  if (node.mult) {
    for (const [stat, v] of Object.entries(node.mult)) {
      if (!v || v === 1) continue;
      const lab = PASSIVE_STAT_LABELS[stat] || stat;
      if (stat === "cooldown" && v < 1) parts.push(`−${Math.round((1 - v) * 100)}% ${lab}`);
      else if (v > 1) parts.push(`+${Math.round((v - 1) * 100)}% ${lab}`);
      else if (v < 1) parts.push(`−${Math.round((1 - v) * 100)}% ${lab}`);
    }
  }
  if (node.flat) {
    for (const [stat, v] of Object.entries(node.flat)) {
      if (!v) continue;
      const lab = PASSIVE_STAT_LABELS[stat] || stat;
      if (stat === "cooldown") parts.push(`−${v}s ${lab}`);
      else if (stat === "duration") parts.push(`+${v}s ${lab}`);
      else if (stat === "armDelay") parts.push(`−${Math.abs(v)}s ${lab}`);
      else if (stat === "illusions" || stat === "split" || stat === "bounce") parts.push(`+${v} ${lab}`);
      else if (stat === "speed") parts.push(`+${v} ${lab}`);
      else parts.push(`+${v} ${lab}`);
    }
  }
  return parts;
}

// Mirror server/sim/skills geometry at base spell stats (+ passives in ownedSpellStats).

function spellRadiusAtLevel(spellId, s) {
  return s?.radius || 0;
}

function spellDetRadiusAtLevel(spellId, s) {
  return s?.detRadius || 0;
}

function spellDurationAtLevel(spellId, s) {
  return s?.duration || 0;
}

/** Heal amount at base spell stats (mirrors server Eff*Heal). */
export function spellHealAtLevel(spellId) {
  const s = SPELLS[spellId];
  if (!s) return 0;
  if (s.drainHeal && s.damage) {
    return Math.round(s.damage * s.drainHeal);
  }
  if (!s.heal) return 0;
  return Math.round(s.heal);
}

/** DMG / Heal / CD / Range / Dur rows for spell tooltips. */
export function spellStatsForTip(spellId) {
  const s = SPELLS[spellId];
  if (!s) return [];
  const stats = [];
  if (spellId === "illusionist") {
    stats.push(["Decoys", 2]);
  } else {
    const baseDmg = s.damage || s.detDamage;
    if (baseDmg) stats.push(["DMG", Math.round(baseDmg)]);
  }
  const heal = spellHealAtLevel(spellId);
  if (heal > 0) stats.push(["Heal", heal]);
  if (s.cooldown) stats.push(["CD", s.cooldown.toFixed(1) + "s"]);
  if (s.range) stats.push(["Range", Math.round(s.range)]);
  const dur = spellDurationAtLevel(spellId, s);
  if (dur > 0) stats.push(["Dur", `${+dur.toFixed(2)}s`]);
  const rad = spellRadiusAtLevel(spellId, s);
  if (rad > 0) stats.push(["Radius", Math.round(rad)]);
  const det = spellDetRadiusAtLevel(spellId, s);
  if (det > 0) stats.push(["Blast", Math.round(det)]);
  return stats;
}

/** Key stats for an owned spell card (passive modifiers applied). */
export function ownedSpellStats(spellId, passives) {
  const s = SPELLS[spellId];
  if (!s) return [];
  const owned = passives?.[spellId] || [];
  const stats = [];
  if (spellId === "illusionist") {
    stats.push(["Decoys", String(illusionDecoyCount(passives, spellId))]);
  } else {
    const baseDmg = s.damage || s.detDamage;
    if (baseDmg) {
      const dmg = Math.round(baseDmg + passiveFlatFromOwned(spellId, owned, "damage"));
      stats.push(["DMG", String(dmg)]);
    }
  }
  const heal = Math.round(spellHealAtLevel(spellId) + passiveFlatFromOwned(spellId, owned, "heal"));
  if (heal > 0) stats.push(["Heal", String(heal)]);
  if (s.cooldown) {
    let cd = s.cooldown - passiveFlatFromOwned(spellId, owned, "cooldown");
    stats.push(["CD", `${Math.max(1, cd).toFixed(1)}s`]);
  }
  if (s.range) {
    const range = Math.round(s.range * passiveMultFromOwned(spellId, owned, "range"));
    stats.push(["Range", String(range)]);
  }
  const durBase = spellDurationAtLevel(spellId, s);
  if (durBase > 0) {
    let dur = durBase * passiveMultFromOwned(spellId, owned, "duration");
    dur += passiveFlatFromOwned(spellId, owned, "duration");
    stats.push(["Dur", `${+dur.toFixed(2)}s`]);
  }
  const radBase = spellRadiusAtLevel(spellId, s);
  if (radBase > 0) {
    const r = Math.round(radBase * passiveMultFromOwned(spellId, owned, "radius"));
    stats.push(["Radius", String(r)]);
  }
  const detBase = spellDetRadiusAtLevel(spellId, s);
  if (detBase > 0) {
    const r = Math.round(detBase * passiveMultFromOwned(spellId, owned, "radius"));
    stats.push(["Blast", String(r)]);
  }
  return stats;
}

/** Host-spell stat rows for a synergy tooltip (passives + synergy-specific tweaks). */
export function synergyHostStats(spellId, passiveId, passives) {
  const stats = passives
    ? ownedSpellStats(spellId, passives)
    : spellStatsForTip(spellId).map(([k, v]) => [k, String(v)]);
  const n = (PASSIVE_TREES[spellId] || []).find(p => p.id === passiveId);
  if (n?.effect === "meteor_nuke") {
    return stats.map(([k, v]) => (k === "Blast" ? [k, String(Math.round(Number(v) * 2))] : [k, v]));
  }
  return stats;
}

/** Shop skill-tree label; multishot synergies keep their unique name plus the shared tag. */
export function passiveShopName(node) {
  if (!node?.name) return "";
  if (node.effect === "multishot" || String(node.id || "").endsWith("_multishot")) {
    return `${node.name} (Multishot)`;
  }
  return node.name;
}

/** spell id → passive node defs (from catalog). */
export const PASSIVE_TREES = {};

export function passiveOwned(passives, spellId, passiveId) {
  const list = passives?.[spellId];
  return Array.isArray(list) && list.includes(passiveId);
}

/** Prior-column passive on the same row (col N requires col N−1). */
export function passiveColPrereqNode(spellId, node) {
  const col = node?.col ?? 0;
  if (col <= 0) return null;
  const row = node?.row ?? 0;
  const tree = PASSIVE_TREES[spellId] || [];
  return tree.find(n => (n.row ?? 0) === row && (n.col ?? 0) === col - 1) || null;
}

export function passivePrereqsMet(passives, spellId, node) {
  const reqs = node?.requires || [];
  if (!reqs.every(r => passiveOwned(passives, spellId, r))) return false;
  if (node?.synergy) return true;
  const prior = passiveColPrereqNode(spellId, node);
  if (prior && !passiveOwned(passives, spellId, prior.id)) return false;
  return true;
}

/** True when spellId is on the action bar. */
export function spellInLoadout(loadout, spellId) {
  return !!spellId && loadout && Object.values(loadout).includes(spellId);
}

/** True when the partner spell is on the action bar (includes planned shop picks). */
export function synergySpellUnlocked(loadout, spellId) {
  return spellInLoadout(loadout, spellId);
}

/** True for default loadout spells (match-start kit). Pass pve=true in PvE mode. */
export function isStarterSpell(spellId, pve = false) {
  const kit = pve ? PVE_START_LOADOUT : START_LOADOUT;
  return !!spellId && Object.values(kit).includes(spellId);
}

/** True when the partner spell is owned on the action bar. */
export function synergySpellLockedIn(loadout, committed, spellId) {
  const slot = slotOfSpell(loadout, spellId);
  if (!slot) return false;
  if (isStarterSpell(spellId)) return true;
  return !!committed?.[slot];
}

/** Partner prereq for a synergy passive node. */
export function synergyPartnerMet(loadout, committed, node) {
  const req = node?.requiresSpell;
  if (!req) return true;
  if (node?.requiresSpellLocked) return synergySpellLockedIn(loadout, committed, req);
  return synergySpellUnlocked(loadout, req);
}

export function synergyUnlockLabel(node) {
  const req = node?.requiresSpell;
  if (!req) return "";
  const name = SPELLS[req]?.name || req;
  if (node?.requiresSpellLocked) return `Requires ${name} locked in`;
  return `Unlocked by ${name}`;
}

/** Active synergy passive on spellId (owned + partner prereq met), or null. */
export function activeSynergyForSpell(loadout, committed, passives, spellId) {
  for (const n of (PASSIVE_TREES[spellId] || [])) {
    if (!n.synergy) continue;
    if (!passiveOwned(passives, spellId, n.id)) continue;
    if (!synergyPartnerMet(loadout, committed, n)) continue;
    return n;
  }
  return null;
}

/** Synergy nodes hosted on this spell's passive tree. */
export function synergiesHostedBy(spellId) {
  return (PASSIVE_TREES[spellId] || []).filter(n => n.synergy && n.requiresSpell);
}

/** Owned spells that pair with a synergy on another owned spell (partner prereq met). */
export function synergyPartnerLitSpellIds(loadout, committed) {
  const owned = new Set(SLOT_ORDER.map(s => loadout[s]).filter(Boolean));
  const lit = new Set();
  for (const hostId of owned) {
    for (const syn of synergiesHostedBy(hostId)) {
      const partner = syn.requiresSpell;
      if (!partner || !owned.has(partner)) continue;
      if (synergyPartnerMet(loadout, committed, syn)) lit.add(partner);
    }
  }
  return lit;
}

/** True when any synergy passive on this spell's tree is already owned. */
export function ownedSynergyOnSpell(passives, spellId) {
  for (const n of (PASSIVE_TREES[spellId] || [])) {
    if (n.synergy && passiveOwned(passives, spellId, n.id)) return true;
  }
  return false;
}

/** True when another synergy on the same tree is already owned (mutually exclusive pick). */
export function synergyExclusiveBlocked(passives, spellId, node) {
  if (!node?.synergy) return false;
  const synergies = synergiesHostedBy(spellId);
  if (synergies.length < 2) return false;
  for (const s of synergies) {
    if (s.id === node.id) continue;
    if (passiveOwned(passives, spellId, s.id)) return true;
  }
  return false;
}

export function synergyExclusiveLabel(passives, spellId, node) {
  for (const s of synergiesHostedBy(spellId)) {
    if (s.id === node.id) continue;
    if (passiveOwned(passives, spellId, s.id)) return `${s.name} owned`;
  }
  return "Pick one synergy";
}

/** Human-readable prereq line for a passive node (spellbook / tooltips). */
export function passiveRequiresLabel(node, spellId) {
  const tree = PASSIVE_TREES[spellId] || [];
  const names = [];
  const seen = new Set();
  for (const rid of (node?.requires || [])) {
    if (seen.has(rid)) continue;
    seen.add(rid);
    names.push(tree.find(n => n.id === rid)?.name || rid);
  }
  const prior = passiveColPrereqNode(spellId, node);
  if (prior && !seen.has(prior.id)) names.push(prior.name || prior.id);
  if (!names.length) return "";
  return `Requires ${names.join(", ")}`;
}

export function spellPassiveSummary(spellId) {
  const tree = PASSIVE_TREES[spellId] || [];
  const upgrades = tree.filter(n => !n.synergy).length;
  const synergies = tree.filter(n => n.synergy).length;
  return { upgrades, synergies, total: tree.length };
}

export function passiveMultFromOwned(spellId, ownedList, stat) {
  let m = 1;
  const tree = PASSIVE_TREES[spellId] || [];
  const owned = new Set(ownedList || []);
  for (const n of tree) {
    if (!owned.has(n.id) || !n.mult) continue;
    const v = n.mult[stat];
    if (v > 0) m *= v;
  }
  return m;
}

export function passiveFlatFromOwned(spellId, ownedList, stat) {
  let sum = 0;
  const tree = PASSIVE_TREES[spellId] || [];
  const owned = new Set(ownedList || []);
  for (const n of tree) {
    if (!owned.has(n.id) || !n.flat) continue;
    const v = n.flat[stat];
    if (v) sum += v;
  }
  return sum;
}

export function illusionDecoyCount(passives, spellId = "illusionist") {
  return Math.max(1, 2 + passiveFlatFromOwned(spellId, passives?.[spellId], "illusions"));
}

// Robe colors for up to 8 wizards
export const ROBES = [
  { robe: "#3b82f6", trim: "#bfdcff", name: "Azure"   },
  { robe: "#ef4444", trim: "#ffc9c9", name: "Crimson" },
  { robe: "#22c55e", trim: "#bbf7c9", name: "Verdant" },
  { robe: "#eab308", trim: "#fff0a8", name: "Amber"   },
  { robe: "#a855f7", trim: "#e7c9ff", name: "Violet"  },
  { robe: "#ec4899", trim: "#ffc9e6", name: "Rose"    },
  { robe: "#14b8a6", trim: "#a8f0e6", name: "Teal"    },
  { robe: "#f97316", trim: "#ffd6a8", name: "Ember"   },
];

// Team palette (1-based). Players on the same team share a colour and take no
// friendly fire. Bots get high team numbers and stay their own team.
export const TEAM_COLORS = ["#e0556a", "#4a90e2", "#46b04a", "#e0a13f", "#a86be0", "#3fd0c0", "#e07ac0", "#9aa0a8"];
export const teamColor = (n) => TEAM_COLORS[(((n - 1) % TEAM_COLORS.length) + TEAM_COLORS.length) % TEAM_COLORS.length];
export const isTeammate = (me, team) => me && team != null && me.team != null && team === me.team;

/** True when any roster member shares a team (2v2+), not FFA (one player per team). */
export const teamsGrouped = (players) => {
  const roster = (players || []).filter(p => !p.boss && !p.bossAdd && !p.dummy);
  if (roster.length < 2) return false;
  const counts = new Map();
  for (const p of roster) counts.set(p.team, (counts.get(p.team) || 0) + 1);
  for (const n of counts.values()) if (n > 1) return true;
  return false;
};

/** One row per team: { team, wins, members[] } sorted by wins desc. */
export const teamStandings = (players) => {
  const roster = (players || []).filter(p => !p.boss && !p.bossAdd && !p.dummy);
  const byTeam = new Map();
  for (const pl of roster) {
    let row = byTeam.get(pl.team);
    if (!row) row = { team: pl.team, wins: 0, members: [] };
    row.members.push(pl);
    row.wins = Math.max(row.wins, pl.wins || 0);
    byTeam.set(pl.team, row);
  }
  return [...byTeam.values()].sort((a, b) => (b.wins - a.wins) || (a.team - b.team));
};
